// Single source of truth for env vars. Add new vars to the schema below;
// they're validated at startup. Set SKIP_ENV_VALIDATION=true to bypass
// (used in CI and Storybook).

import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

import { SUBSCRIPTION_PROVIDER_KEYS } from './subscriptionProviders';

const REGEX = {
  SANITY_API_VERSION: /\d{4}-\d{2}-\d{2}/,
  SHOPIFY_STOREFRONT_API_VERSION: /\d{4}-\d{2}/,
};

const optionalBoolean = () =>
  z
    .string()
    .refine((s) => s === String(true) || s === String(false))
    .optional()
    .transform((s) => s === String(true));

const environments = z.enum(['development', 'preview', 'production']);

// Set SKIP_ENV_VALIDATION=true to bypass explicitly. Storybook contexts (both
// `storybook build` and `storybook dev` set STORYBOOK=true) auto-skip so the
// Vite preview build doesn't crash on missing server secrets when stories
// transitively import this module - that would otherwise ship a half-built
// storybook (manager only, no iframe.html) as a "successful" Vercel deploy.
//
// `skipValidation` disables the zod parse but keeps `runtimeEnv` wiring and
// its coercions/fallbacks, so `env.*` still behaves consistently across
// contexts (e.g. `NEXT_PUBLIC_*_ENABLED` booleans stay booleans).
export const env = createEnv({
  /*
   * Serverside Environment variables, not available on the client.
   * Will throw if you access these variables on the client.
   */
  server: {
    NEXTAUTH_URL: z.url().optional(),
    NEXTAUTH_SECRET: z.string(),

    SHOPIFY_MULTIPASS_TOKEN: z.string(),
    SHOPIFY_CUSTOMER_ACCOUNT_API_CLIENT_ID: z.string(),
    SHOPIFY_CUSTOMER_ACCOUNT_API_CLIENT_SECRET: z.string(),

    KLAVIYO_API_KEY: z.string(),

    VERCEL_ENV: environments,

    ISR_TTL_SECONDS: z.coerce.number().int().positive(),

    TEST_PROMOTION_PAGE: z.string().optional(),
    TEST_PARTNER_PAGE: z.string().optional(),
  },
  /*
   * Environment variables available on the client (and server).
   *
   * 💡 You'll get type errors if these are not prefixed with NEXT_PUBLIC_.
   */
  client: {
    NEXT_PUBLIC_VERCEL_ENV: environments,
    NEXT_PUBLIC_VERCEL_URL: z.string().optional(),

    NEXT_PUBLIC_SITE_URL: z.url(),

    NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN: z.string(),
    NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION: z
      .string()
      .regex(
        REGEX.SHOPIFY_STOREFRONT_API_VERSION,
        'Invalid api version format',
      ),
    NEXT_PUBLIC_SHOPIFY_HOST: z.string(),
    NEXT_PUBLIC_SHOPIFY_SHOP_ID: z.string(),

    NEXT_PUBLIC_SHOPIFY_SUBSCRIPTION_APPNAME: z.enum(
      SUBSCRIPTION_PROVIDER_KEYS as [string, ...string[]],
    ),

    NEXT_PUBLIC_SANITY_PROJECT_ID: z.string(),
    NEXT_PUBLIC_SANITY_DATASET: z.string(),
    NEXT_PUBLIC_SANITY_API_VERSION: z
      .string()
      .regex(REGEX.SANITY_API_VERSION, 'Invalid api version format'),

    NEXT_PUBLIC_SENTRY_DSN: z.url().optional(),
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: z.string().optional(),

    NEXT_PUBLIC_COOKIEBOT_DEFAULT_CBID: z.string().optional(),

    NEXT_PUBLIC_CONVERGE_PIXEL_URL: z.string().optional(),

    NEXT_PUBLIC_VWO_ACCOUNT_ID: z.string().optional(),

    NEXT_PUBLIC_GA_ID: z.string().optional(),

    NEXT_PUBLIC_JUNIP_STORE_KEY: z.string(),

    NEXT_PUBLIC_TIMELINE_API: z.string(), // SHOP-8528: to be removed

    NEXT_PUBLIC_CLOUDINARY_CLOUDNAME: z.string(),

    NEXT_PUBLIC_TRUEMED_QUALIFICATION_URL: z.string().optional(),

    NEXT_PUBLIC_UPSELL_CHOICES: z.coerce.number().gte(0).lte(2).optional(),

    NEXT_PUBLIC_FREE_SHIPPING_THRESHOLD_USD: z.coerce
      .number()
      .positive()
      .optional(),

    NEXT_PUBLIC_ANALYTICS_EVENTS_CART_UPSELL_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_COUNTRY_SELECTOR_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_FRONTROWMD_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_GWP_PROMO_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_LONGEVITY_MONTH_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_OOS_BADGE_DISABLED: optionalBoolean(),
    NEXT_PUBLIC_OOS_NOTIFICATION_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_PRODUCT_CARD_ATC_ENABLED: z
      .enum(['ALL', 'SKINCARE', 'SUPPLEMENTS'])
      .optional(),
    NEXT_PUBLIC_REVIEWS_DISABLED: optionalBoolean(),
    NEXT_PUBLIC_REVIEWS_TRAITS_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_REVIEWS_UPVOTE_WIDGET: z.enum(['ALL', 'UP', 'NONE']).optional(),
    NEXT_PUBLIC_SITE_TOGGLE_CALLOUT_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_SITE_SEARCH_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_SKINCARE_CARDS_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_VIBE_GALLERIES_ENABLED: optionalBoolean(),
    NEXT_PUBLIC_POLAR_PIXEL_ENDPOINT: z.url().optional(),
    NEXT_PUBLIC_SKINCARE_PDP_PICKER: z
      .enum(['PURCHASE_TYPE', 'FORMAT'])
      .optional(),
  },
  /*
   * Due to how Next.js bundles environment variables on Edge and Client,
   * we need to manually destructure them to make sure all are included in bundle.
   *
   * 💡 You'll get type errors if not all variables from `server` & `client` are included here.
   */
  runtimeEnv: {
    // Server env vars START
    NEXTAUTH_URL: process.env.NEXTAUTH_URL,
    NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET,

    SHOPIFY_MULTIPASS_TOKEN: process.env.SHOPIFY_MULTIPASS_TOKEN,
    SHOPIFY_CUSTOMER_ACCOUNT_API_CLIENT_ID:
      process.env.SHOPIFY_CUSTOMER_ACCOUNT_API_CLIENT_ID,
    SHOPIFY_CUSTOMER_ACCOUNT_API_CLIENT_SECRET:
      process.env.SHOPIFY_CUSTOMER_ACCOUNT_API_CLIENT_SECRET,

    KLAVIYO_API_KEY: process.env.KLAVIYO_API_KEY,

    VERCEL_ENV: process.env.VERCEL_ENV,

    ISR_TTL_SECONDS: process.env.ISR_TTL_SECONDS,

    TEST_PROMOTION_PAGE: process.env.TEST_PROMOTION_PAGE,
    TEST_PARTNER_PAGE: process.env.TEST_PARTNER_PAGE,
    // Server env vars END

    // Client env vars START
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV || 'development',
    NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,

    NEXT_PUBLIC_SITE_URL:
      process.env.NEXT_PUBLIC_SITE_URL ||
      `https://${process.env.NEXT_PUBLIC_VERCEL_URL as string}`,

    NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN:
      process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
    NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION:
      process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION,
    NEXT_PUBLIC_SHOPIFY_HOST: process.env.NEXT_PUBLIC_SHOPIFY_HOST,
    NEXT_PUBLIC_SHOPIFY_SHOP_ID: process.env.NEXT_PUBLIC_SHOPIFY_SHOP_ID,
    NEXT_PUBLIC_SHOPIFY_SUBSCRIPTION_APPNAME:
      process.env.NEXT_PUBLIC_SHOPIFY_SUBSCRIPTION_APPNAME,

    NEXT_PUBLIC_SANITY_PROJECT_ID: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID,
    NEXT_PUBLIC_SANITY_DATASET: process.env.NEXT_PUBLIC_SANITY_DATASET,
    NEXT_PUBLIC_SANITY_API_VERSION: process.env.NEXT_PUBLIC_SANITY_API_VERSION,

    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,

    NEXT_PUBLIC_COOKIEBOT_DEFAULT_CBID:
      process.env.NEXT_PUBLIC_COOKIEBOT_DEFAULT_CBID,

    NEXT_PUBLIC_CONVERGE_PIXEL_URL: process.env.NEXT_PUBLIC_CONVERGE_PIXEL_URL,

    NEXT_PUBLIC_VWO_ACCOUNT_ID: process.env.NEXT_PUBLIC_VWO_ACCOUNT_ID,

    NEXT_PUBLIC_GA_ID: process.env.NEXT_PUBLIC_GA_ID,

    NEXT_PUBLIC_JUNIP_STORE_KEY: process.env.NEXT_PUBLIC_JUNIP_STORE_KEY,

    NEXT_PUBLIC_TIMELINE_API: process.env.NEXT_PUBLIC_TIMELINE_API, // SHOP-8528: to be removed

    NEXT_PUBLIC_CLOUDINARY_CLOUDNAME:
      process.env.NEXT_PUBLIC_CLOUDINARY_CLOUDNAME,

    NEXT_PUBLIC_TRUEMED_QUALIFICATION_URL:
      process.env.NEXT_PUBLIC_TRUEMED_QUALIFICATION_URL,

    NEXT_PUBLIC_UPSELL_CHOICES: process.env.NEXT_PUBLIC_UPSELL_CHOICES,

    NEXT_PUBLIC_FREE_SHIPPING_THRESHOLD_USD:
      process.env.NEXT_PUBLIC_FREE_SHIPPING_THRESHOLD_USD,

    NEXT_PUBLIC_ANALYTICS_EVENTS_CART_UPSELL_ENABLED:
      process.env.NEXT_PUBLIC_ANALYTICS_EVENTS_CART_UPSELL_ENABLED,
    NEXT_PUBLIC_COUNTRY_SELECTOR_ENABLED:
      process.env.NEXT_PUBLIC_COUNTRY_SELECTOR_ENABLED,
    NEXT_PUBLIC_FRONTROWMD_ENABLED: process.env.NEXT_PUBLIC_FRONTROWMD_ENABLED,
    NEXT_PUBLIC_GWP_PROMO_ENABLED: process.env.NEXT_PUBLIC_GWP_PROMO_ENABLED,
    NEXT_PUBLIC_LONGEVITY_MONTH_ENABLED:
      process.env.NEXT_PUBLIC_LONGEVITY_MONTH_ENABLED,
    NEXT_PUBLIC_OOS_BADGE_DISABLED: process.env.NEXT_PUBLIC_OOS_BADGE_DISABLED,
    NEXT_PUBLIC_OOS_NOTIFICATION_ENABLED:
      process.env.NEXT_PUBLIC_OOS_NOTIFICATION_ENABLED,
    NEXT_PUBLIC_PRODUCT_CARD_ATC_ENABLED:
      process.env.NEXT_PUBLIC_PRODUCT_CARD_ATC_ENABLED,
    NEXT_PUBLIC_REVIEWS_DISABLED: process.env.NEXT_PUBLIC_REVIEWS_DISABLED,
    NEXT_PUBLIC_REVIEWS_TRAITS_ENABLED:
      process.env.NEXT_PUBLIC_REVIEWS_TRAITS_ENABLED,
    NEXT_PUBLIC_REVIEWS_UPVOTE_WIDGET:
      process.env.NEXT_PUBLIC_REVIEWS_UPVOTE_WIDGET || 'NONE',
    NEXT_PUBLIC_SITE_TOGGLE_CALLOUT_ENABLED:
      process.env.NEXT_PUBLIC_SITE_TOGGLE_CALLOUT_ENABLED,
    NEXT_PUBLIC_SITE_SEARCH_ENABLED:
      process.env.NEXT_PUBLIC_SITE_SEARCH_ENABLED,
    NEXT_PUBLIC_SKINCARE_CARDS_ENABLED:
      process.env.NEXT_PUBLIC_SKINCARE_CARDS_ENABLED,
    NEXT_PUBLIC_VIBE_GALLERIES_ENABLED:
      process.env.NEXT_PUBLIC_VIBE_GALLERIES_ENABLED,
    NEXT_PUBLIC_POLAR_PIXEL_ENDPOINT:
      process.env.NEXT_PUBLIC_POLAR_PIXEL_ENDPOINT,
    NEXT_PUBLIC_SKINCARE_PDP_PICKER:
      process.env.NEXT_PUBLIC_SKINCARE_PDP_PICKER,
  },
  skipValidation:
    process.env.SKIP_ENV_VALIDATION === 'true' ||
    process.env.STORYBOOK === 'true',
});

export const isDev = env.NEXT_PUBLIC_VERCEL_ENV === 'development';
export const isProd = env.NEXT_PUBLIC_VERCEL_ENV === 'production';
